/**
 * 批量填写房价
 */
import React, { useImperativeHandle, useRef, useState } from 'react';
import { Modal, Table, Button, message } from 'antd';
import intl from 'react-intl-universal';

import InputWithCoupon from '@/components/Reservation/NumberInput/InputWithCoupon';
import { PRICE_REG } from '@/config/reg';
import styles from './index.module.scss';

interface IRecord {
  indexId: string;
  roomTypeName: string; // 房型名称
  roomNo?: string; // 房间号
  deposit: string; // 押金
  inputPrice: string; // 价格
}

export interface BatchModifyPriceModalRef {
  // sceneType 0 预订 1入住
  openModal: (roomList: Partial<IRecord>[], sceneType: 0 | 1) => void;
}

interface IProps {
  innerRef: React.Ref<BatchModifyPriceModalRef>;
  tipNode?: React.ReactNode;
  onEnd: (list: IRecord[]) => void;
}

const Index: React.FC<IProps> = (props) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [sceneType, setSceneType] = useState(0);
  const [dataSource, setDataSource] = useState<IRecord[]>([]);
  const submitStatusRef = useRef(false);
  const moreThanOne = dataSource.length >= 2;

  const { innerRef, tipNode, onEnd } = props;

  const handleCancelClick = () => {
    setVisible(false);
  };

  const handleSubmitClick = () => {
    if (submitStatusRef.current) return;
    submitStatusRef.current = true;
    try {
      const updateList: IRecord[] = [];
      dataSource.forEach((item) => {
        const { inputPrice, deposit } = item;
        // 房间房价或者押金有更新
        if (inputPrice || deposit) {
          if (!PRICE_REG.test(inputPrice)) {
            throw new Error(intl.get('reservation_error_room_price_invalid'));
          }
          if (!PRICE_REG.test(deposit)) {
            throw new Error(intl.get('reservation_error_room_deposit_invalid'));
          }
          updateList.push(item);
        }
      });
      if (updateList && updateList.length) {
        onEnd(updateList);
      }
      setVisible(false);
    } catch (err) {
      message.error(err.message);
      submitStatusRef.current = false;
    }
  };

  // 修改押金
  const handleItemDepositChange: (inputValue: string, id: string) => void = (inputValue, id) => {
    setDataSource(
      dataSource.map((item) => {
        if (item.indexId === id) return { ...item, deposit: inputValue };
        return item;
      }),
    );
  };

  // 设置相同押金
  const setAllDepositToSame = () => {
    const targetValue = dataSource[0].deposit;
    if (PRICE_REG.test(targetValue)) {
      setDataSource(
        dataSource.map((item) => {
          return { ...item, deposit: targetValue };
        }),
      );
    }
  };

  // 押金输入框
  const renderDepositCell = (id: string, record: IRecord, index: number) => {
    return (
      <div>
        <InputWithCoupon
          withCoupon={false}
          value={record.deposit}
          onChange={(val) => handleItemDepositChange(val, id)}
        />
        <span>
          {moreThanOne && index === 0 ? (
            <Button type="link" onClick={setAllDepositToSame}>
              {intl.get('reservation_cmp_batch_price_table_content_same')}
            </Button>
          ) : (
            ''
          )}
        </span>
      </div>
    );
  };

  // 修改价格
  const handleItemPriceChange: (inputValue: string, id: string) => void = (inputValue, id) => {
    setDataSource(
      dataSource.map((item) => {
        if (item.indexId === id) return { ...item, inputPrice: inputValue };
        return item;
      }),
    );
  };

  // 设置相同价格
  const setAllPriceToSame = () => {
    const targetValue = dataSource[0].inputPrice;
    if (PRICE_REG.test(targetValue)) {
      setDataSource(
        dataSource.map((item) => {
          return { ...item, inputPrice: targetValue };
        }),
      );
    }
  };

  // 价格输入框
  const renderPriceCell = (id: string, record: IRecord, index: number) => {
    return (
      <div>
        <InputWithCoupon
          withCoupon={false}
          value={record.inputPrice}
          onChange={(val) => handleItemPriceChange(val, id)}
        />
        <span>
          {moreThanOne && index === 0 ? (
            <Button type="link" onClick={setAllPriceToSame}>
              {intl.get('reservation_cmp_batch_price_table_content_same')}
            </Button>
          ) : (
            ''
          )}
        </span>
      </div>
    );
  };

  const getTableColumns = () => {
    const columnConfig = {
      0: [
        {
          title: intl.get('reservation_cmp_batch_price_table_column_price'),
          dataIndex: 'indexId',
          width: 240,
          render: renderPriceCell,
        },
      ],
      1: [
        {
          title: intl.get('app_reservation_deposit_amount'),
          dataIndex: 'indexId',
          width: 240,
          render: renderDepositCell,
        },
        {
          title: intl.get('reservation_cmp_batch_price_table_column_price'),
          dataIndex: 'indexId',
          width: 240,
          render: renderPriceCell,
        },
      ],
    };
    return [
      { title: intl.get('app_constants_room_type'), dataIndex: 'roomTypeName' },
      { title: intl.get('app_constants_room_no'), dataIndex: 'roomNo' },
    ].concat(columnConfig[sceneType]);
  };

  useImperativeHandle(
    innerRef,
    () => ({
      openModal: (roomList, type) => {
        submitStatusRef.current = false;
        if (roomList && roomList.length) {
          setDataSource(
            roomList.map((item) => {
              return {
                indexId: item.indexId,
                roomTypeName: item.roomTypeName,
                roomNo: item.roomNo || intl.get('app_reservation_room_queue_no'),
                deposit: '',
                inputPrice: '',
              };
            }),
          );
          setSceneType(type);
          setVisible(true);
        }
      },
    }),
    [],
  );

  return (
    <Modal
      title={intl.get('reservation_cmp_batch_price_title')}
      okText={intl.get('reservation_cmp_batch_price_submit_btn')}
      width={860}
      wrapClassName={styles.batchModifyModal}
      open={visible}
      onCancel={handleCancelClick}
      onOk={handleSubmitClick}
    >
      <div>{tipNode}</div>
      <Table
        columns={getTableColumns()}
        dataSource={dataSource}
        scroll={{ x: 'max-content' }}
        className={styles.roomListTable}
        pagination={false}
        rowKey="indexId"
      />
    </Modal>
  );
};

export default Index;
